import { useSnackbar } from "@homebound/beam";
import { useCallback, useEffect, useState } from "react";
import { CsvRow } from "src/components/CsvUploader";
import { isCsv } from "src/components/CsvUtils";
import { CostType, SaveItemTemplateItemVersionInput } from "src/generated/graphqlTypes";
import {
  ItemTemplateImportModalQuery,
  useItemTemplateImportModalLazyQuery,
} from "src/routes/settings/itemTemplates/ImportItemTemplateItemsModal.generated";
import { stripEmptyString } from "src/utils";
import { ItemTemplateApi } from "./api/ItemTemplateApi";

/** Helper to map CSV field values (names) to ids/enums. */
export type ImportLookups = {
  itemCodeToId: Record<string, string>;
  itemIdToVersion: Record<string, number>;
  costTypeToEnum: Record<string, CostType>;
  locationNameToId: Record<string, string>;
  locationIdToVersion: Record<string, number>;
  uomNameToId: Record<string, string>;
  defaultLocationId: string;
  defaultUomId: string;
  options: Array<{ id: string; name: string; code: string }>;
};

type MappedInputErrors = {
  kind: "errors";
  errors: string[];
};

type MappedInputSuccess = {
  kind: "success";
  inputs: SaveItemTemplateItemVersionInput[];
};

type MappedInputResult = MappedInputErrors | MappedInputSuccess;

/** Maps CSV rows to GQL inputs for item template items. */
export function mapToInputs(lookups: ImportLookups, rows: CsvRow[], planPackage?: boolean): MappedInputResult {
  const { header, columnIndexes } = findHeaders(rows);
  if (header === undefined) {
    return { kind: "errors", errors: ["No headers founds; please check format of CSV and upload again"] };
  }

  // Get the raw string[] data into an array of JSON objects
  const importRows = rows
    .slice(1, rows.length) // slice drops the header
    .filter((row) => row.data.length > 1) // drop any empty rows / new lines, i.e. `row=[""]`
    .map((row, i) => {
      // Create a JSON version of the data[]; surely react-papaparse could do this for us...
      const importRow = { index: i } as ImportRow;
      header.data.forEach((header, j) => {
        const inputKey = csvHeaderToInputKey[header];
        if (inputKey) importRow[inputKey] = row.data[columnIndexes[j]]?.trim();
      });
      return importRow;
    });

  // Keep a list of errors, granted all we can do client-side is if the lookup failed
  const errors: string[] = [];

  const inputs = importRows.map((row, i) => {
    const input: SaveItemTemplateItemVersionInput = {};
    const isRemove = row.quantity?.toLowerCase() === "remove";
    // Format itemCode with 3 decimals, e.g. 5101.01 => 5101.010
    const itemCode = formatItemCode(row.itemCode);
    input.itemId = itemCode && lookups.itemCodeToId[itemCode];
    input.name = row.name;
    input.costType = row.costType ? lookups.costTypeToEnum[row.costType.toLowerCase()] : undefined;
    input.locationId = row.location ? lookups.locationNameToId[row.location.toLowerCase()] : lookups.defaultLocationId;
    input.unitOfMeasureId = (row.uom && lookups.uomNameToId[row.uom.toLowerCase()]) || lookups.defaultUomId;
    input.specifications = row.specifications || undefined;
    input.tradePartnerNote = row.tradePartnerNote || undefined;
    input.internalNote = row.internalNote || undefined;
    input.quantity = isRemove ? 0 : (row.quantity && quantity(row.quantity)) || 0;
    input.totalCostInCents = (row.totalCostInDollars && dollarsToCents(row.totalCostInDollars)) || 0;
    if (!input.totalCostInCents && input.quantity && row.unitCostInDollars) {
      const unitCostInCents = dollarsToCents(row.unitCostInDollars);
      input.totalCostInCents = Math.round(unitCostInCents * input.quantity);
    }
    input.baseProductId = row.blueprintProductId || undefined;
    input.bidItemCode = row.bidItemCode || undefined;
    input.isSelection = !!row.blueprintProductId;
    // Spec Option is a name column, while the others are id columns
    const specOptionId = lookups.options.find(
      (rpo) => rpo.name.toLowerCase() === row.specOptionName?.toLowerCase(),
    )?.id;
    // Map the legacy csv "separate columns" into the new `optionIds`
    input.optionIds = [
      ...(row.optionIds?.split(",") ?? []), // Allow multiple options
      row.elevationId,
      specOptionId,
    ]
      .map(stripEmptyString)
      .unique()
      .compact();
    if (isRemove) {
      input.remove = isRemove;
    }

    const [invalidFields, unsetFields] = (
      [
        ["itemId", "itemCode"],
        ["costType", "costType"],
        ["locationId", "location"],
      ] as const
    )
      .filter(([inputField]) => !input[inputField])
      .map(([_, csvField]) => {
        return {
          csvField,
          specifiedValue: row[csvField],
        };
      })
      .partition(({ csvField }) => !!row[csvField]);

    invalidFields.forEach(({ csvField, specifiedValue }) =>
      errors.push(`Row ${i + 2} specified a ${csvField} '${specifiedValue}' that does not exist in Blueprint`),
    );
    if (unsetFields.nonEmpty) {
      // Use +1 for the header and +1 to be 1-based, so +2
      errors.push(`Row ${i + 2} is missing required fields ${unsetFields.map(({ csvField }) => csvField).join(", ")}`);
    }

    return input;
  });

  if (errors.length > 0) {
    return { kind: "errors", errors };
  }

  return { kind: "success", inputs };
}

function findHeaders(rowData: CsvRow[]): { header: CsvRow | undefined; columnIndexes: number[] } {
  const requiredHeaders = ["Item Code", "Cost Type"];
  // Does the first row have all our required headers?
  const headerRow = rowData[0];
  if (!headerRow || !requiredHeaders.every((name) => headerRow.data.includes(name))) {
    return { header: undefined, columnIndexes: [] };
  }
  // Support the headers being at random indexes
  const columnIndexes: number[] = [];
  const validHeaders: string[] = [];
  headerRow.data.forEach((header) => {
    const inputKey = csvHeaderToInputKey[header];
    if (inputKey) {
      validHeaders.push(header);
      columnIndexes.push(headerRow.data.indexOf(header));
    }
  });
  headerRow.data = validHeaders;
  return { header: headerRow, columnIndexes };
}

const csvHeaderToInputKey: Record<string, Exclude<keyof ImportRow, "index">> = {
  "Item Code": "itemCode",
  "Cost Type": "costType",
  "Item Name": "name",
  Location: "location",
  Specifications: "specifications",
  "Trade Note": "tradePartnerNote",
  "Internal Note": "internalNote",
  Cost: "totalCostInDollars",
  "Unit Cost": "unitCostInDollars",
  Units: "quantity",
  "Unit of Measure": "uom",
  "Blueprint Product Id": "blueprintProductId",
  "Bid Item": "bidItemId",
  "Bid Item Code": "bidItemCode",
  "Spec Level": "specOptionName",
  "Elevation Id": "elevationId",
  "Option Id": "optionId",
  "Option Ids": "optionIds",
};

/** A step between CsvRow `string[]` and SaveItemTemplateItemVersionInput. */
export type ImportRow = {
  index: number;
  itemCode: string | undefined; // look up entity by cost
  costType: string | undefined; // look up enum by name
  name: string;
  location: string | undefined; // look up enum by name
  specifications: string | undefined;
  tradePartnerNote: string | undefined;
  internalNote: string | undefined;
  totalCostInDollars: string | undefined;
  unitCostInDollars: string | undefined;
  quantity: string | undefined; // can be empty, will be set to 0 if lump sum uom
  uom: string | undefined; // look up entity by name
  blueprintProductId: string | undefined;
  bidItemId: string | undefined;
  bidItemCode: string | undefined;
  elevationId: string | undefined;
  specOptionName: string | undefined;
  optionId: string | undefined;
  optionIds: string | undefined;
};

function dollarsToCents(toParse: string): number {
  return Math.round(parseFloat(toParse.replace(/[$,]/gm, "")) * 100);
}

function quantity(toParse: string): number {
  return Math.round(parseFloat(toParse.replace(/,/g, "")));
}

// Item Code needs to be 3 decimals, so 1.1 -> 1.100, 1.11 -> 1.110
// exported for testing
export function formatItemCode(itemCode: string | undefined) {
  if (itemCode) {
    const itemCodeParts = itemCode.split(".");
    if (itemCodeParts.length === 2) {
      const decimalPart = itemCodeParts[1];
      if (decimalPart.length === 1) {
        return `${itemCode}00`;
      } else if (decimalPart.length === 2) {
        return `${itemCode}0`;
      }
    }
  }
  return itemCode;
}

function createItemTemplateLineItemLookupMaps(data: ItemTemplateImportModalQuery): ImportLookups {
  return {
    itemCodeToId: data.items.keyBy(
      (i) => i.fullCode,
      (i) => i.id,
    ),
    locationNameToId: data.locations
      .uniqueBy((l) => l.name)
      .keyBy(
        (l) => l.name.toLowerCase(),
        (l) => l.id,
      ),
    itemIdToVersion: data.items.keyBy(
      (i) => i.id,
      (i) => i.version,
    ),
    locationIdToVersion: data.locations.keyBy(
      (l) => l.id,
      (l) => l.version,
    ),
    uomNameToId: data.unitsOfMeasure.keyBy(
      (uom) => uom.name.toLowerCase(),
      (uom) => uom.id,
    ),
    costTypeToEnum: data.costTypes.keyBy(
      (ct) => ct.name.toLowerCase(),
      (ct) => ct.code,
    ),
    defaultUomId: data.unitsOfMeasure.filter((uom) => !uom.useQuantity)[0]?.id,
    defaultLocationId: data.locations.filter((l) => l.isWholeHouse)[0]?.id,
    // Global v1 templates don't have a ReadyPlan/options
    options: data.itemTemplate.readyPlan?.options ?? [],
  };
}

function mapInputToLookups(data: ItemTemplateImportModalQuery, csvRows: CsvRow[]): MappedInputResult {
  const lookups = createItemTemplateLineItemLookupMaps(data);
  return mapToInputs(lookups, csvRows);
}

export function useItemTemplateItemsUploader(itApi: ItemTemplateApi, opts?: { autoImport?: boolean }) {
  const { autoImport } = opts ?? {};
  const [errors, setErrors] = useState<string[]>([]);
  const [getImportData, query] = useItemTemplateImportModalLazyQuery();
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [importDataReady, setImportDataReady] = useState(false);
  const [mappedInputResult, setMappedInputResult] = useState<MappedInputResult | undefined>(undefined);
  const { triggerNotice } = useSnackbar();

  const saveItemTemplateMutation = useCallback(
    async (result: MappedInputSuccess) => {
      const { data } = await itApi.addItiv(result.inputs);
      triggerNotice({
        message: `${data?.saveItemTemplateItemVersions.itemTemplateItemVersions.length} items were successfully uploaded`,
        icon: "success",
      });
      if (data?.saveItemTemplateItemVersions.deleted.nonEmpty)
        triggerNotice({
          message: `${data?.saveItemTemplateItemVersions.deleted.length} items could not be uploaded due to mismatching Ready Plan Options.`,
          icon: "warning",
          persistent: true,
        });
    },
    [itApi, triggerNotice],
  );

  useEffect(() => {
    // Once our lazy query has resolved we begin the import process(lookups and mapping our csv rows)
    if (query.data && !query.loading) {
      // Used as a switch to prevent the user importing before the importData is ready
      setImportDataReady(true);
      // We reset the errors array every time we upload a new file
      setErrors([]);
      const result = mapInputToLookups(query.data, csvRows);
      if (result.kind === "errors") {
        setErrors(result.errors);
      }
      // If we're auto importing and no errors have been found, then we can 'autoImport' the items, otherwise we wait for the user to click the import button
      void (autoImport && result.kind === "success" ? saveItemTemplateMutation(result) : setMappedInputResult(result));
    }
  }, [query, saveItemTemplateMutation, csvRows, autoImport]);

  const addError = useCallback(
    (err: unknown) => {
      setErrors((existingErrors) => existingErrors.concat(String(err)));
    },
    [setErrors],
  );

  const handleOnDrop = useCallback(
    async (rows: CsvRow[], file?: { type: string; name: string }) => {
      if (!isCsv(file)) {
        return setErrors(["Incorrect file type. Please make sure the file is a .csv file"]);
      }
      // lets the user drop the same file again, otherwise nothing happens, with no feedback
      setImportDataReady(false);
      setCsvRows([]);

      await getImportData({ variables: { itemTemplateId: itApi.templateId } });
      setCsvRows(rows);
    },
    [getImportData, setCsvRows, setImportDataReady, itApi],
  );

  const handleOnClick = useCallback(async () => {
    if (mappedInputResult?.kind === "success") {
      await saveItemTemplateMutation(mappedInputResult);
    }
  }, [saveItemTemplateMutation, mappedInputResult]);

  return { errors, addError, handleOnDrop, handleOnClick, importDataReady };
}
